// Copyright Caproc Oy
// System name: Aqua-Net
// System module: Company
// Author: TS
//
// 16.4.2026 TS (claude) Initial version - cross-company user list and Outlook mailing list tool

import React from "react";
import { Message, Header, Button, Table, Segment, Dimmer, Loader, Image, Dropdown, Icon } from "semantic-ui-react";
import Shell from "./Shell";
import { authAxios } from "../../utils";
import { setActivity } from "../../store/actions/auth";
import { withRouter, Redirect } from "react-router-dom";
import { connect } from "react-redux";
import { withTranslation } from 'react-i18next';
import { adminMailingListURL } from "../../constants";


class CompanyMailingList extends React.Component {
    state = {
        users: null,
        companies: null,
        emailList: "",
        userCount: 0,
        selectedCompany: "",
        error: null,
        loading: false,
        copied: false
    };

    componentDidMount() {
        this.props.setActivity();
        this.fetchMailingList();
    }

    fetchMailingList = () => {
        this.setState({ loading: true });
        const companyId = this.state.selectedCompany || null;
        authAxios
            .get(adminMailingListURL(companyId))
            .then(res => {
                this.setState({
                    users: res.data.users,
                    companies: res.data.companies,
                    emailList: res.data.email_list,
                    userCount: res.data.user_count,
                    error: null,
                    loading: false
                });
            })
            .catch(err => {
                this.setState({
                    loading: false,
                    error: err
                });
            });
    };

    handleCompanyChange = (e, { value }) => {
        this.setState({ selectedCompany: value, copied: false }, () => {
            this.fetchMailingList();
        });
    };

    handleCopyEmails = () => {
        navigator.clipboard.writeText(this.state.emailList).then(() => {
            this.setState({ copied: true });
            setTimeout(() => this.setState({ copied: false }), 3000);
        });
    };

    render() {
        const { error, loading, users, companies, emailList, userCount, selectedCompany, copied } = this.state;
        const { isAuthenticated, t } = this.props;
        if (!isAuthenticated) {
            return <Redirect to="/login" />;
        }

        const companyOptions = companies ? [
            { key: 'all', value: '', text: t("All companies") },
            ...companies.map(c => ({ key: c.id, value: c.id, text: c.name }))
        ] : [];

        return (
            <Shell>
                <Header as="h3">{t("Mailing list")}</Header>

                {companies && (
                    <Dropdown
                        placeholder={t("Select company")}
                        selection
                        options={companyOptions}
                        value={selectedCompany}
                        onChange={this.handleCompanyChange}
                        style={{ marginBottom: "1em" }}
                    />
                )}

                {emailList && (
                    <Segment>
                        <Header as="h4">
                            {t("Email addresses")} ({userCount})
                            <Button
                                floated="right"
                                color={copied ? "green" : "blue"}
                                onClick={this.handleCopyEmails}
                                icon
                                labelPosition="left"
                                size="small"
                            >
                                <Icon name={copied ? "check" : "copy"} />
                                {copied ? t("Copied!") : t("Copy to clipboard")}
                            </Button>
                        </Header>
                        <p style={{ wordBreak: "break-all", color: "#666", marginTop: "0.5em" }}>{emailList}</p>
                    </Segment>
                )}

                {error && (
                    <Message
                        error
                        header={t("There was an error")}
                        content={JSON.stringify(error)}
                    />
                )}
                {loading && (
                    <Segment>
                        <Dimmer active inverted>
                            <Loader inverted>Loading</Loader>
                        </Dimmer>
                        <Image src="https://react.semantic-ui.com/images/wireframe/short-paragraph.png" />
                    </Segment>
                )}

                {users && (
                    <Table striped celled>
                        <Table.Header>
                            <Table.Row>
                                <Table.HeaderCell>{t("First name")}</Table.HeaderCell>
                                <Table.HeaderCell>{t("Last name")}</Table.HeaderCell>
                                <Table.HeaderCell>{t("Email")}</Table.HeaderCell>
                                <Table.HeaderCell>{t("Phone")}</Table.HeaderCell>
                                <Table.HeaderCell>{t("Company")}</Table.HeaderCell>
                                <Table.HeaderCell>{t("Role")}</Table.HeaderCell>
                            </Table.Row>
                        </Table.Header>
                        <Table.Body>
                            {users.map((user) => (
                                <Table.Row key={user.id}>
                                    <Table.Cell>{user.first_name}</Table.Cell>
                                    <Table.Cell>{user.last_name}</Table.Cell>
                                    <Table.Cell>{user.email}</Table.Cell>
                                    <Table.Cell>{user.phone_number}</Table.Cell>
                                    <Table.Cell>
                                        {user.userroles
                                            .map(r => r.company_name)
                                            .filter(Boolean)
                                            .filter((v, i, a) => a.indexOf(v) === i)
                                            .join(', ')}
                                    </Table.Cell>
                                    <Table.Cell>
                                        {user.userroles
                                            .map(r => `${r.role_name} (${r.company_name})`)
                                            .filter(Boolean)
                                            .join(', ')}
                                    </Table.Cell>
                                </Table.Row>
                            ))}
                        </Table.Body>
                    </Table>
                )}
            </Shell>
        );
    }
}

const mapStateToProps = state => {
    return {
        activeCompanyId: state.company.activeCompanyId,
        isAuthenticated: state.auth.token !== null
    };
};

const mapDispatchToProps = dispatch => {
    return {
        setActivity: () => dispatch(setActivity())
    };
};

export default withRouter(
    connect(
        mapStateToProps,
        mapDispatchToProps
    )(withTranslation('companymailinglist')(CompanyMailingList))
);
